import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  BarChart3, TrendingUp, TrendingDown, IndianRupee, Loader2,
  AlertCircle, Users, Clock, ArrowUpRight, ArrowDownRight,
  FileText, CreditCard, Download, Building2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FeatureGate } from "../components/FeatureGate";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const { isAuthenticated, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && isAuthenticated) fetchData();
    if (!authLoading && !isAuthenticated) setLoading(false);
  }, [isAuthenticated, authLoading, selectedCompany]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCompany !== "all" ? `?company_id=${selectedCompany}` : "";
      const [analyticsRes, compRes] = await Promise.all([
        axios.get(`${API}/builder/analytics/dashboard${params}`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setData(analyticsRes.data);
      setCompanies(compRes.data.companies || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <BarChart3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign in to view analytics</h2>
          <p className="text-slate-500 mb-6">Get insights into your business performance.</p>
          <Button onClick={login} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-prompt-btn">Sign in with Google</Button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const maxTrend = data?.monthly_trend ? Math.max(...data.monthly_trend.map(t => Math.max(t.sales, t.purchases)), 1) : 1;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="analytics-page">
      <FeatureGate feature="analytics">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Analytics</h1>
            <p className="text-sm text-slate-500">Business overview and insights</p>
          </div>
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-52" data-testid="company-filter">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {!data || data.total_invoices === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>No data yet</h3>
            <p className="text-slate-500 mb-4">Create invoices to see your analytics.</p>
            <Button onClick={() => navigate("/create")} className="bg-blue-600 hover:bg-blue-700 text-white">
              Create First Invoice
            </Button>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Total Sales", value: fmt(data.total_sales), icon: <ArrowUpRight className="w-5 h-5" />, color: "bg-emerald-50 text-emerald-600", border: "border-emerald-100" },
                { label: "Total Purchases", value: fmt(data.total_purchases), icon: <ArrowDownRight className="w-5 h-5" />, color: "bg-red-50 text-red-600", border: "border-red-100" },
                { label: "Receivable", value: fmt(data.outstanding_receivable), icon: <IndianRupee className="w-5 h-5" />, color: "bg-amber-50 text-amber-600", border: "border-amber-100" },
                { label: "Payable", value: fmt(data.outstanding_payable), icon: <CreditCard className="w-5 h-5" />, color: "bg-blue-50 text-blue-600", border: "border-blue-100" },
              ].map((kpi, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-white rounded-2xl border ${kpi.border} shadow-sm p-5`}
                  data-testid={`kpi-card-${i}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${kpi.color} mb-3`}>{kpi.icon}</div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{kpi.label}</p>
                  <p className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{kpi.value}</p>
                </motion.div>
              ))}
            </div>

            {/* GST Summary */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="gst-collected">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase">GST Collected</p>
                    <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(data.total_gst_collected)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="gst-paid">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase">GST Paid</p>
                    <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(data.total_gst_paid)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Trend Chart */}
            {data.monthly_trend?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="monthly-trend">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Monthly Trend</h3>
                <div className="flex items-end gap-3 h-48">
                  {data.monthly_trend.map((month, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex gap-1 items-end" style={{ height: "160px" }}>
                        <div
                          className="flex-1 bg-blue-500 rounded-t-md transition-all"
                          style={{ height: `${(month.sales / maxTrend) * 100}%`, minHeight: month.sales > 0 ? "4px" : "0" }}
                          title={`Sales: ${fmt(month.sales)}`}
                        />
                        <div
                          className="flex-1 bg-purple-400 rounded-t-md transition-all"
                          style={{ height: `${(month.purchases / maxTrend) * 100}%`, minHeight: month.purchases > 0 ? "4px" : "0" }}
                          title={`Purchases: ${fmt(month.purchases)}`}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">{month.month?.slice(5)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500" /> Sales</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-purple-400" /> Purchases</span>
                </div>
              </div>
            )}

            {/* Top Unpaid */}
            {data.top_unpaid_customers?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="top-unpaid">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Top Unpaid Customers</h3>
                </div>
                <div className="space-y-3">
                  {data.top_unpaid_customers.map((customer, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600">{i + 1}</div>
                        <span className="text-sm font-medium text-slate-900">{customer.name}</span>
                      </div>
                      <span className="text-sm font-bold text-red-600">{fmt(customer.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center" data-testid="stat-invoices">
                <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{data.total_invoices}</p>
                <p className="text-xs text-slate-500">Total Invoices</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center" data-testid="stat-monthly-sales">
                <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{fmt(data.monthly_sales)}</p>
                <p className="text-xs text-slate-500">This Month Sales</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center" data-testid="stat-overdue">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{data.overdue_count}</p>
                <p className="text-xs text-slate-500">Overdue Invoices</p>
              </div>
            </div>
          </>
        )}
      </div>
      </FeatureGate>
    </div>
  );
}
